import { useState } from 'react'
import { Badge, Card, DataState, PageHeader } from '@/components/Common'
import { CheckField, FormSection, TextArea, TextField } from '@/components/Form'
import { ConfirmDialog, Modal } from '@/components/Modal'
import { useAuth } from '@/lib/auth'
import { useResource } from '@/lib/resource'
import { useFetch } from '@/lib/hooks'
import type { Department, Facility, FacilityTree } from '@/lib/api'

/**
 * Facilities and departments.
 *
 * This is a tenancy boundary, not a taxonomy. Two hospitals both have a
 * Radiology department and both have a scanner called CT1; the whole reason
 * departments are keyed on (facility, code) rather than on code alone is that
 * those are different departments with different schedules.
 */
export function Facilities() {
  const tenancy = useFetch<FacilityTree[]>('/api/v1/tenancy')
  const facilities = useResource<Facility>('/api/v1/facilities')
  const departments = useResource<Department>('/api/v1/departments')
  const { can } = useAuth()

  const [editingFacility, setEditingFacility] = useState<Facility | 'new' | null>(null)
  const [editingDepartment, setEditingDepartment] = useState<
    { department: Department | null; facilityId: number } | null
  >(null)
  const [deleting, setDeleting] = useState<
    { kind: 'facility' | 'department'; id: number; name: string } | null
  >(null)

  const mayEdit = can('config_admin')
  const tree = tenancy.data ?? []
  const reload = () => {
    tenancy.reload()
    facilities.reload()
    departments.reload()
  }

  return (
    <>
      <PageHeader
        title="Facilities"
        subtitle="Sites and their departments — the tenancy boundary everything else is scoped by."
        actions={
          mayEdit && (
            <button className="btn btn-primary" onClick={() => setEditingFacility('new')}>
              Add facility
            </button>
          )
        }
      />

      {(facilities.saveError || departments.saveError) && (
        <div className="notice notice-error">
          {facilities.saveError ?? departments.saveError}
        </div>
      )}

      <Card>
        <p className="card-note">
          A code is short, stable and operator-facing — <code>SMC</code> — and it
          is what rules, the <code>$AE_FACILITY</code> token and a foreign
          source's alias all refer to. Changing one is a rename that touches
          configuration people have memorised, so pick it once.
        </p>

        <DataState
          loading={tenancy.loading}
          error={tenancy.error}
          empty={tree.length === 0}
          emptyMessage="No facilities. On a single-site installation you can leave this empty — everything works unscoped. Add one before a second site exists."
        />

        {tree.map((facility) => (
          <div key={facility.id} className="card-note">
            <div className="card-head">
              <h3>
                <span className="mono">{facility.code}</span> — {facility.name}{' '}
                {!facility.enabled && <Badge state="DISABLED" />}
              </h3>
              {mayEdit && (
                <span className="actions">
                  <button
                    className="btn btn-small"
                    onClick={() =>
                      setEditingDepartment({ department: null, facilityId: facility.id })
                    }
                  >
                    Add department
                  </button>
                  <button className="btn btn-small" onClick={() => setEditingFacility(facility)}>
                    Edit
                  </button>
                  <button
                    className="btn btn-small btn-danger-quiet"
                    onClick={() =>
                      setDeleting({ kind: 'facility', id: facility.id, name: facility.code })
                    }
                  >
                    Delete
                  </button>
                </span>
              )}
            </div>

            {facility.dicom_institution_name && (
              <div className="hint">
                Sent as Institution Name (0008,0080):{' '}
                <span className="mono">{facility.dicom_institution_name}</span>
              </div>
            )}
            {facility.notes && <div className="hint">{facility.notes}</div>}

            {facility.departments.length === 0 ? (
              <p className="hint">No departments.</p>
            ) : (
              <table className="ae-table">
                <thead>
                  <tr>
                    <th>Code</th>
                    <th>Department</th>
                    <th>Notes</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {facility.departments.map((department) => (
                    <tr
                      key={department.id}
                      className={department.enabled ? undefined : 'row-muted'}
                    >
                      <td className="mono">{department.code}</td>
                      <td>
                        {department.name}
                        {!department.enabled && <> <Badge state="DISABLED" /></>}
                      </td>
                      <td className="hint">{department.notes}</td>
                      <td className="actions">
                        {mayEdit && (
                          <>
                            <button
                              className="btn btn-small"
                              onClick={() =>
                                setEditingDepartment({
                                  department,
                                  facilityId: facility.id,
                                })
                              }
                            >
                              Edit
                            </button>
                            <button
                              className="btn btn-small btn-danger-quiet"
                              onClick={() =>
                                setDeleting({
                                  kind: 'department',
                                  id: department.id,
                                  name: `${facility.code}/${department.code}`,
                                })
                              }
                            >
                              Delete
                            </button>
                          </>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        ))}
      </Card>

      {editingFacility && (
        <FacilityEditor
          facility={editingFacility === 'new' ? null : editingFacility}
          saving={facilities.saving}
          error={facilities.saveError}
          onClose={() => {
            setEditingFacility(null)
            facilities.clearError()
          }}
          onSave={async (draft) => {
            const saved =
              editingFacility === 'new'
                ? await facilities.create(draft)
                : await facilities.update(editingFacility.id, draft)
            if (saved) {
              setEditingFacility(null)
              reload()
            }
          }}
        />
      )}

      {editingDepartment && (
        <DepartmentEditor
          department={editingDepartment.department}
          facilityId={editingDepartment.facilityId}
          saving={departments.saving}
          error={departments.saveError}
          onClose={() => {
            setEditingDepartment(null)
            departments.clearError()
          }}
          onSave={async (draft) => {
            const saved = editingDepartment.department
              ? await departments.update(editingDepartment.department.id, draft)
              : await departments.create(draft)
            if (saved) {
              setEditingDepartment(null)
              reload()
            }
          }}
        />
      )}

      {deleting && (
        <ConfirmDialog
          title={`Delete ${deleting.kind} '${deleting.name}'?`}
          confirmLabel={`Delete ${deleting.kind}`}
          destructive
          busy={facilities.saving || departments.saving}
          error={facilities.saveError ?? departments.saveError}
          onCancel={() => setDeleting(null)}
          onConfirm={async () => {
            const ok =
              deleting.kind === 'facility'
                ? await facilities.remove(deleting.id)
                : await departments.remove(deleting.id)
            if (ok) {
              setDeleting(null)
              reload()
            }
          }}
        >
          <p>
            This is refused while anything still refers to it — devices,
            worklists, rules or scheduled steps. The message will name what.
          </p>
          <p className="hint">
            Disabling instead keeps the history intelligible: a scheduled step
            attributed to a deleted facility becomes unattributable, and an
            unattributed step is invisible to every scoped query.
          </p>
        </ConfirmDialog>
      )}
    </>
  )
}

function FacilityEditor({
  facility,
  saving,
  error,
  onClose,
  onSave,
}: {
  facility: Facility | null
  saving: boolean
  error: string | null
  onClose: () => void
  onSave: (draft: Record<string, unknown>) => void
}) {
  const [code, setCode] = useState(facility?.code ?? '')
  const [name, setName] = useState(facility?.name ?? '')
  const [institution, setInstitution] = useState(facility?.dicom_institution_name ?? '')
  const [enabled, setEnabled] = useState(facility?.enabled ?? true)
  const [notes, setNotes] = useState(facility?.notes ?? '')

  return (
    <Modal
      title={facility ? `Edit facility '${facility.code}'` : 'Add facility'}
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button
            className="btn btn-primary"
            disabled={saving || !code || !name}
            onClick={() =>
              onSave({
                code,
                name,
                dicom_institution_name: institution,
                enabled,
                notes,
              })
            }
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
        </>
      }
    >
      {error && <div className="notice notice-error">{error}</div>}
      <FormSection title="Identity">
        <div className="form-grid">
          <TextField
            label="Code"
            value={code}
            onChange={setCode}
            maxLength={16}
            required
            showCount
            hint="Short and stable. Used in rules, in $AE_FACILITY, and by every source alias."
          />
          <TextField label="Name" value={name} onChange={setName} required />
        </div>
        <TextField
          label="DICOM Institution Name"
          value={institution}
          onChange={setInstitution}
          maxLength={64}
          hint="Sent as (0008,0080) when a scheduled step carries none of its own, and stamped by the modality into every image. Usually shorter than the name, and often dictated by what the archive already holds — check before tidying it."
        />
        <CheckField label="Enabled" checked={enabled} onChange={setEnabled} />
      </FormSection>
      <FormSection title="Notes">
        <TextArea label="Notes" value={notes} onChange={setNotes} maxLength={1000} />
      </FormSection>
    </Modal>
  )
}

function DepartmentEditor({
  department,
  facilityId,
  saving,
  error,
  onClose,
  onSave,
}: {
  department: Department | null
  facilityId: number
  saving: boolean
  error: string | null
  onClose: () => void
  onSave: (draft: Record<string, unknown>) => void
}) {
  const [code, setCode] = useState(department?.code ?? '')
  const [name, setName] = useState(department?.name ?? '')
  const [enabled, setEnabled] = useState(department?.enabled ?? true)
  const [notes, setNotes] = useState(department?.notes ?? '')

  return (
    <Modal
      title={department ? `Edit department '${department.code}'` : 'Add department'}
      subtitle="Codes are unique within a facility, not globally."
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button
            className="btn btn-primary"
            disabled={saving || !code || !name}
            onClick={() => onSave({ facility_id: facilityId, code, name, enabled, notes })}
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
        </>
      }
    >
      {error && <div className="notice notice-error">{error}</div>}
      <div className="form-grid">
        <TextField label="Code" value={code} onChange={setCode} maxLength={16} required />
        <TextField label="Name" value={name} onChange={setName} required />
      </div>
      <CheckField label="Enabled" checked={enabled} onChange={setEnabled} />
      <TextArea label="Notes" value={notes} onChange={setNotes} maxLength={1000} />
    </Modal>
  )
}
